// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { ChevronRight } from "lucide-react"
import { Link, useNavigate } from "react-router-dom"
import { cn } from "@sglara/cn"
import { useForgeArena } from "@/queries/queries.forges"
import { formatDate } from "@/lib/formatDateTime"
import { CopyableId } from "@/views/forges/copyableId"

const formatScore = (n: number): string => n.toFixed(3)

// Truncated display form for an agent id (full id stays on the link's
// accessible name + copy affordance). Head-truncate keeps the id
// scannable without losing the family prefix.
const shortAgentId = (id: string): string =>
  id.length > 14 ? `${id.slice(0, 13)}…` : id

// Origin cycle short form: `cycle-<4hex>…` for a canonical `cycle-<uuid>`,
// verbatim for a non-conforming id (never truncating what we can't parse).
const shortOriginCycle = (id: string): string => {
  const match = /^cycle-([0-9a-f]{4})[0-9a-f]*-/i.exec(id)
  return match ? `cycle-${match[1]}…` : id
}

export interface ForgeArenaProps {
  forgeId: string
  nCycles: number
}

export const ForgeArena = ({ nCycles, forgeId }: ForgeArenaProps) => {
  const { data, isSuccess, isError } = useForgeArena(forgeId)
  const navigate = useNavigate()

  // Arena absent for a forge that has never run a cycle — the spec footer
  // still tells the user what the forge will optimise (US §17).
  if (nCycles === 0) return null

  // Server owns the best-first ordering (direction-aware) — never re-sort here.
  const items = data?.items ?? []

  return (
    <section
      role="region"
      aria-label="Arena"
      className="box-glow flex flex-col px-5 py-[18px]"
    >
      <span className="mb-3 font-mono text-[10px] uppercase tracking-[0.14em] text-text-tertiary">
        Top 5 arena · best agents
      </span>

      {isError ? (
        <div
          role="alert"
          className="rounded-md border border-failed/40 bg-failed/10 p-4 text-failed-fg"
        >
          Could not load the arena.
        </div>
      ) : items.length === 0 ? (
        // "No result yet" only once the arena read has settled empty — a
        // still-pending fetch must not flash the empty state (US §8).
        isSuccess ? (
          <div className="text-sm text-text-tertiary">No result yet.</div>
        ) : null
      ) : (
        <ol className="flex flex-col">
          {items.map((entry, idx) => {
            const rank = idx + 1
            const isTop = rank === 1
            return (
              <li
                key={entry.agent_id}
                data-agent-id={entry.agent_id}
                data-rank={rank}
                onClick={() => navigate(`/agents/${entry.agent_id}`)}
                className={cn(
                  "grid cursor-pointer grid-cols-[34px_56px_1fr_auto] items-center gap-x-3 gap-y-1.5 transition-colors",
                  isTop
                    ? "mb-1.5 mt-0.5 rounded-[11px] border border-[rgba(69,240,200,0.28)] bg-[rgba(69,240,200,0.07)] px-3 py-[11px]"
                    : "border-b border-border px-1.5 py-[9px] last:border-b-0 hover:bg-table-row-hover",
                )}
              >
                <span
                  className={cn(
                    "font-mono font-bold",
                    isTop
                      ? "text-[15px] text-mint"
                      : "text-[13px] text-text-tertiary",
                  )}
                >
                  #{rank}
                </span>
                <span
                  data-testid={isTop ? "arena-top-score" : undefined}
                  className={cn(
                    "font-mono font-bold",
                    isTop
                      ? "text-[21px] text-mint"
                      : "text-[15px] text-foreground",
                  )}
                >
                  {formatScore(entry.score)}
                </span>
                <div className="flex min-w-0 flex-col gap-0.5">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <Link
                      to={`/agents/${entry.agent_id}`}
                      aria-label={entry.agent_id}
                      onClick={(e) => e.stopPropagation()}
                      className="truncate font-mono text-xs text-foreground hover:underline"
                    >
                      {shortAgentId(entry.agent_id)}
                    </Link>
                    <CopyableId id={entry.agent_id} label="agent id" />
                  </div>
                  <span className="truncate font-mono text-[10.5px] text-text-tertiary">
                    from {shortOriginCycle(entry.cycle_id)} ·{" "}
                    {formatDate(entry.submitted_at)}
                  </span>
                </div>
                <ChevronRight
                  aria-hidden
                  className="size-4 text-text-tertiary"
                />
              </li>
            )
          })}
        </ol>
      )}
    </section>
  )
}
